import type { QRAnswerItem } from '@scripsteam/scrips-signal-ds';

/**
 * The one place a finding can always land — Contract E.
 *
 * An ORPHAN is a finding whose class no template item accepts. The rule Samer settled on
 * 2026-08-21: **never dropped, never blocking.** It stays visible, and if the clinician adds it, it
 * lands in free text with its dictation origin marked.
 *
 * ── WHY IT IS AT THE ENCOUNTER LEVEL AND NOT PER SECTION ──────────────────────────────────────
 *
 * A per-section narrative item already exists — `encounter-section-chrome.tsx`, the ADDED NOTES
 * editor bound to a `-narrative` linkId (PROD-1368), which honestly disables itself when the
 * section authored none. That is correct behaviour and stays.
 *
 * But it is not GUARANTEED. Where a template authored no narrative item, an orphan has nowhere to
 * go and the affordance greys out — so the finding is lost, which is the outcome the contract
 * forbids. Requiring every template to author one presumes a Template-Studio feedback loop that
 * does not exist (§10).
 *
 * So: one sink per encounter, outside the template. **No Studio dependency** — ruling R6.
 *
 * ── HOW IT PERSISTS WITHOUT A TEMPLATE ITEM ───────────────────────────────────────────────────
 *
 * `toQuestionnaireResponseResource` maps `response.answers` straight to wire items with no template
 * check, so a reserved linkId round-trips through the existing save with nothing new server-side.
 * The note builder has to be taught to emit it, because `responseToNote` walks the TEMPLATE and
 * would otherwise never look — see `appendSinkSection`.
 */

/**
 * The reserved linkId. Deliberately prefixed so it cannot collide with an authored item: Template
 * Studio emits `sec-<guid>` and step-prefixed names, never this.
 *
 * Treated as a published constant, not an implementation detail — it appears in stored
 * QuestionnaireResponses, so changing it orphans every note already written.
 */
export const ENCOUNTER_NARRATIVE_LINK_ID = 'encounter-narrative';

/** The heading the sink renders under in the note. */
export const ENCOUNTER_NARRATIVE_LABEL = 'Additional notes';

/** One dictated line waiting for a home. */
export interface OrphanEntry {
  /** The clinical text, as dictated. */
  text: string;
  /**
   * Why it had nowhere to go — shown to the clinician, never invented.
   *
   * `measured-finding` is the one Samer reported: *"normal bloodd pressure wa snever captuered,
   * physical examination normal was never captuered"*. The SOAP buckets are diagnoses /
   * medications / labs / radiology / vaccinations, and a normal examination result is none of
   * those — so it had no card and no narrative field, and simply vanished. The analyser had it
   * all along, paired to its value by a `ValueOfExamination` edge.
   */
  reason:
    | 'unclassified'
    | 'no-template-item'
    | 'measured-finding'
    /**
     * WITHHELD FROM THE DIAGNOSIS LIST, AND THEREFORE OWED A HOME.
     *
     * `reconcile-proposals` withholds a diagnosis the analyzer contradicts — a symptom the model
     * filed as a diagnosis, a family member's condition, something nobody asserted. Cross-model
     * review found the panel was converting those to card ids to remove and dropping the rest:
     * *"a provider-needed diagnosis can disappear entirely whenever it is not independently
     * repeated in a mapped narrative field."*
     *
     * My reason for not sinking them was that the chief-complaint narrative already carries them.
     * That is an ASSUMPTION, and a wrong one in the common case: the narrative only lands if the
     * encounter's template declares `ambient-chief-complaint`, which most templates do not. So the
     * finding vanished — silently, and precisely because a guard removed it.
     *
     * A withheld finding is a decision about where something belongs. It was never a decision to
     * lose it.
     */
    | 'withheld-not-a-diagnosis'
    /**
     * A VITAL THE MODEL HEARD AND NOBODY CONFIRMED (B2).
     *
     * `AmbientVitals` is nine strings — "120/80", "afebrile", "normal" — and the vitals STORE is
     * what the downstream workflow reads and what NABIDH exports on sign-lock. So a model-heard
     * reading may never populate a vitals field, however well-formed it looks: the ones that parse
     * cleanly are exactly the ones that would then be indistinguishable from a cuff reading
     * somebody took.
     *
     * It is still a thing the clinician said, so it is owed a home. `to-dictated-vitals.ts` puts it
     * here, marked unconfirmed in the text itself.
     */
    | 'dictated-vital'
    /**
     * A MEASURED PAIR THE NOTE DID NOT CARRY, OR CARRIED UNDER THE WRONG ANALYTE.
     *
     * `value-pair-check.ts` compares every `ValueOfExamination` pair TA4H found against the lines
     * actually going into the note. A pair that is missing, or whose value ended up beside a
     * different examination, is listed here explicitly rather than left to be noticed — silently
     * dropping half a pair is the failure that check exists to catch, and an entry saying so is
     * the only form the finding can take once it has no value to report.
     */
    | 'pair-not-carried'
    /**
     * PROD-2538. AUDIO THAT WAS NEVER CAPTURED.
     *
     * The connection dropped and part of the consultation was not transcribed. The note carries the
     * gap as a line of its own, because a note that reads as complete across a hole is a partial
     * record signed as a whole one. See `transcript-gap.ts`.
     */
    | 'transcript-gap';
}

/**
 * The text as the sink stores it: each line trimmed, blank lines dropped, joined by a newline.
 * An entry can hold more than one line of text; `appendToSink` writes this form and `sinkHasLine`
 * matches against it, so the write and the match always agree.
 */
function normaliseEntry(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('\n');
}

/**
 * True when the sink already holds `text` as WHOLE lines: the dictated form `appendToSink` writes
 * (`<text> (dictated)`) or the same words typed on lines of their own. Lines are split on newlines
 * and trimmed, then compared exactly. A substring test is wrong here: "heart rate: 7" is inside
 * "heart rate: 78 (dictated)", so a real line would never be offered or appended.
 *
 * A MULTI-LINE entry is matched as a consecutive block of sink lines. `appendToSink` writes it that
 * way, with the "(dictated)" mark on its last line, so a retry finds it and adds nothing.
 */
export function sinkHasLine(existing: string | undefined, text: string): boolean {
  const t = normaliseEntry(text);
  if (t.length === 0 || !existing) return false;
  const want = t.split('\n');
  // Blank lines are dropped on BOTH sides: a note saved before #737 can hold a multi-line entry with
  // a blank line inside it, and that entry must still match on retry.
  const lines = existing
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const last = want.length - 1;
  for (let i = 0; i + want.length <= lines.length; i += 1) {
    const hit = want.every((w, k) => {
      const l = lines[i + k];
      return l === w || (k === last && l === `${w} (dictated)`);
    });
    if (hit) return true;
  }
  return false;
}

/**
 * Append orphan text to the encounter narrative, preserving what is already there.
 *
 * APPEND, NEVER REPLACE. The sink holds the clinician's own added notes as well as dictated
 * orphans, and a dictated line must not overwrite something a person typed. Returns the existing
 * answer unchanged when there is nothing to add, so a caller cannot accidentally mark an encounter
 * dirty by asking.
 *
 * Each dictated line is marked at the point it lands. A clinician reading the note six months later
 * cannot ask the app where a sentence came from — the text has to say so itself, which is the same
 * reason the note marks AI-drafted narrative.
 */
export function appendToSink(existing: string | undefined, entries: readonly OrphanEntry[]): string | undefined {
  const additions = entries
    .map((e) => normaliseEntry(e.text))
    .filter((t) => t.length > 0)
    .filter((t) => !sinkHasLine(existing, t)); // idempotent: a re-analysis must not double it

  if (additions.length === 0) return existing;

  const marked = additions.map((t) => `${t} (dictated)`);
  const base = (existing ?? '').trimEnd();
  return base.length > 0 ? `${base}\n${marked.join('\n')}` : marked.join('\n');
}

/**
 * The sink as a QuestionnaireResponse answer, ready to merge.
 *
 * `aiAuthored` is NOT set. The sink holds the clinician's typed notes too, so flagging the whole
 * answer would mark their words as the model's — a worse lie than not marking at all. Per-line
 * provenance lives in the text, via `appendToSink`.
 */
export function sinkAnswer(value: string): QRAnswerItem {
  return { linkId: ENCOUNTER_NARRATIVE_LINK_ID, value };
}

/** Read the current sink value out of a response's answers. */
export function readSink(answers: readonly QRAnswerItem[] | undefined): string | undefined {
  const hit = (answers ?? []).find((a) => a.linkId === ENCOUNTER_NARRATIVE_LINK_ID);
  return typeof hit?.value === 'string' ? hit.value : undefined;
}

/** How a dictated line reads once it has landed — the exact text `appendToSink` writes. */
export function landedLine(text: string): string {
  return `${normaliseEntry(text)} (dictated)`;
}

/**
 * The entries `appendToSink` WILL add to `existing` — the ones a clinician is owed a card for
 * (PROD-2447). Same filter, same order, so the card list and the note can never disagree about what
 * landed: an entry already in the sink (a re-analysis repeating itself) lands nothing and gets no
 * second card. A line repeated inside one batch gets one card.
 */
export function landingEntries(existing: string | undefined, entries: readonly OrphanEntry[]): OrphanEntry[] {
  const seen = new Set<string>();
  return entries.filter((e) => {
    const t = normaliseEntry(e.text);
    if (t.length === 0 || sinkHasLine(existing, t) || seen.has(t)) return false;
    seen.add(t);
    return true;
  });
}

/** The reversal line Undo appends. Named so the note, the tests and the card agree on it. */
export function reversalLine(text: string): string {
  return `Withdrawn by clinician: ${text.trim()}`;
}

/**
 * UNDO IS A REVERSAL, NOT A DELETION (PROD-2447, Samer 2026-09-23).
 *
 * The original landing stays in the record — the sink is append-only, the same convention
 * `appendToSink` follows — and a line saying the clinician withdrew it is appended after it. A
 * reader of the signed note sees both what ambient recovered and that the clinician took it back.
 *
 * Idempotent: a second Undo of the same line appends nothing. And because the original text is
 * still present, `appendToSink` will not re-add it on a later re-analysis, so an undone finding
 * stays undone.
 */
export function appendReversal(existing: string | undefined, text: string): string | undefined {
  const t = text.trim();
  if (t.length === 0) return existing;
  const line = reversalLine(t);
  if ((existing ?? '').includes(line)) return existing;
  const base = (existing ?? '').trimEnd();
  return base.length > 0 ? `${base}\n${line}` : line;
}
